import { lazy, Suspense, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { initFirebaseSync } from "@/lib/store";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import HomePage from "@/pages/HomePage";
import MarketplacePage from "@/pages/MarketplacePage";
import ProductPage from "@/pages/ProductPage";
import SellPage from "@/pages/SellPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import CartPage from "@/pages/CartPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import FavouritesPage from "@/pages/FavouritesPage";
import NotFoundPage from "@/pages/NotFoundPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";

// Dashboards sit behind auth and are visited far less than the storefront, so
// they're code-split out of the main bundle.
const SellerDashboardPage = lazy(() => import("@/pages/seller/SellerDashboardPage"));
const SellerListingsPage = lazy(() => import("@/pages/seller/SellerListingsPage"));
const AddProductPage = lazy(() => import("@/pages/seller/AddProductPage"));
const SellerOrdersPage = lazy(() => import("@/pages/seller/SellerOrdersPage"));
const SellerReviewsPage = lazy(() => import("@/pages/seller/SellerReviewsPage"));
const SellerPayoutsPage = lazy(() => import("@/pages/seller/SellerPayoutsPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const AdminProductsPage = lazy(() => import("@/pages/admin/AdminProductsPage"));
const AdminManageStockPage = lazy(() => import("@/pages/admin/AdminManageStockPage"));
const AdminSellersPage = lazy(() => import("@/pages/admin/AdminSellersPage"));

// React Router doesn't scroll to `#hash` targets on client-side navigation
// the way a full page load does, so it's handled manually here: scroll to
// the matching element when a hash is present, otherwise reset to top.
function ScrollRestoration() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Login/signup are a focused, single-viewport experience — the persistent
// footer would push the page taller than 100vh and force a scrollbar.
const NO_FOOTER_PATHS = new Set(["/login", "/signup"]);

export default function App() {
  useEffect(() => initFirebaseSync(), []);
  const { pathname } = useLocation();

  return (
    <div className="min-h-dvh flex flex-col bg-[var(--nm-bg)] text-[var(--nm-ink)]">
      <ScrollRestoration />
      <Navbar />
      <main className="flex-1">
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/sell" element={<SellPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/favourites" element={<FavouritesPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/dashboard/seller" element={<SellerDashboardPage />} />
            <Route path="/dashboard/seller/listings" element={<SellerListingsPage />} />
            <Route path="/dashboard/seller/add-product" element={<AddProductPage />} />
            <Route path="/dashboard/seller/orders" element={<SellerOrdersPage />} />
            <Route path="/dashboard/seller/reviews" element={<SellerReviewsPage />} />
            <Route path="/dashboard/seller/payouts" element={<SellerPayoutsPage />} />
            <Route path="/dashboard/admin" element={<AdminDashboardPage />} />
            <Route path="/dashboard/admin/products" element={<AdminProductsPage />} />
            <Route path="/dashboard/admin/manage" element={<AdminManageStockPage />} />
            <Route path="/dashboard/admin/sellers" element={<AdminSellersPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {!NO_FOOTER_PATHS.has(pathname) && <Footer />}
    </div>
  );
}
